import React, { useMemo, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const OccupancyChart = ({ allOccupancyData, currOccupancyData, propertyType }) => {
    const [viewMode, setViewMode] = useState('percent'); // 'percent' | 'beds'

    const occupancyData = propertyType === "All Property" ? allOccupancyData : currOccupancyData;

    const {
        sortedGraphData,
        currentMonthData,
        occupiedPercent,
        vacantPercent,
        getMonthYearLabel,
    } = useFinancialMonthData(occupancyData.graphData);

    const processedData = {
        labels: sortedGraphData.map(item => getMonthYearLabel(item)),
        datasets: [
            {
                label: "Occupied",
                data: sortedGraphData.map(item => item.occupied),
                backgroundColor: "#2f855a",
                borderRadius: 4,
            },
            {
                label: "Vacant",
                data: sortedGraphData.map(item => item.vacant),
                backgroundColor: "#F56565",
                borderRadius: 4,
            },
        ],
    };

    const options1 = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const item = sortedGraphData[context.dataIndex];
                        const total = item.occupied + item.vacant;
                        const percent = total
                            ? Math.round((context.raw / total) * 100)
                            : 0;

                        return `${context.dataset.label}: ${context.raw}/${total} (${percent}%)`;
                    },
                },
            },
            title: {
                display: true,
                text: 'Bed Occupancy Overview',
                font: {
                    size: 16
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    display: false
                },
                ticks: {
                    stepSize: 10,
                    callback: function (value) {
                        return value === 0 ? '0' : `${value}`;
                    }
                }
            }
        }
    };

    const lineChartData = {
        labels: sortedGraphData.map(item => getMonthYearLabel(item)),
        datasets: [
            {
                label: 'Occupancy %',
                data: sortedGraphData.map(item => {
                    const total = item.occupied + item.vacant;
                    return total ? Math.round((item.occupied / total) * 100) : 0;
                }),
                borderColor: '#3B82F6',
                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                tension: 0.4,
                fill: true,
                pointRadius: 3,
                pointHoverRadius: 5,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: viewMode === 'beds',
                position: 'bottom',
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    font: { size: 11 },
                    color: '#6B7280',
                },
            },
            tooltip: {
                callbacks: {
                    label: ctx =>
                        viewMode === 'percent'
                            ? `Occupancy: ${ctx.raw}%`
                            : `${ctx.dataset.label}: ${ctx.raw}`,
                },
            },
        },
        scales: {
            y: viewMode === 'percent'
                ? {
                    min: 0,
                    max: 100,
                    ticks: {
                        callback: v => `${v}%`,
                        color: '#6B7280',
                    },
                    grid: { color: 'rgba(0,0,0,0.04)' },
                }
                : {
                    beginAtZero: true,
                    ticks: { color: '#6B7280' },
                    grid: { color: 'rgba(0,0,0,0.04)' },
                },
            x: {
                grid: { display: false },
                ticks: { color: '#6B7280', font: { size: 11 } },
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
    };


    const percentDataset = {
        labels: sortedGraphData.map(item => getMonthYearLabel(item)),
        datasets: [
            {
                label: 'Occupancy %',
                data: sortedGraphData.map(item => {
                    const total = item.occupied + item.vacant;
                    return total ? Math.round((item.occupied / total) * 100) : 0;
                }),
                borderColor: '#3B82F6',
                backgroundColor: 'rgba(59,130,246,0.08)',
                tension: 0.4,
                fill: true,
                pointRadius: 3,
                pointHoverRadius: 5,
            },
        ],
    };

    const bedsDataset = {
        labels: sortedGraphData.map(item => getMonthYearLabel(item)),
        datasets: [
            {
                label: 'Occupied Beds',
                data: sortedGraphData.map(item => item.occupied),
                borderColor: '#22C55E',
                backgroundColor: 'rgba(34,197,94,0.08)',
                tension: 0.35,
                fill: false,
                pointRadius: 2,
            },
            {
                label: 'Vacant Beds',
                data: sortedGraphData.map(item => item.vacant),
                borderColor: '#EF4444',
                backgroundColor: 'rgba(239,68,68,0.08)',
                tension: 0.35,
                fill: false,
                pointRadius: 2,
            },
        ],
    };

    const chartData = viewMode === 'percent'
        ? percentDataset
        : bedsDataset;



    const totalBeds = occupancyData?.graphData?.reduce((sum, item) => sum + item.occupied + item.vacant, 0) || 0;
    const currentMonth = new Date().getMonth() + 1; // 1-12 format
    const currentData = occupancyData?.graphData?.find(item => Number(item.month) === currentMonth) || { occupied: 0, vacant: 0 };
    const prevMonthNumber = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevMonthData = occupancyData?.graphData?.find(item => Number(item.month) === prevMonthNumber) || { occupied: 0, vacant: 0 };
    const changeFromLastMonth = currentData.occupied - prevMonthData.occupied;

    return (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4">
            {/* <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Occupancy</h3>
            </div> */}
            <div className="flex justify-between items-center mb-4">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Occupancy
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Beds occupied vs vacant
                        </p>
                    </div>
                </div>
                <div className="flex items-center bg-gray-100 rounded-full p-0.5">
                    {['percent', 'beds'].map(mode => (
                        <button
                            key={mode}
                            onClick={() => setViewMode(mode)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-all
                            ${viewMode === mode
                                    ? 'bg-white shadow-sm text-gray-900'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {mode === 'percent' ? 'Occupancy %' : 'Beds'}
                        </button>
                    ))}
                </div>
            </div>
            <div className="h-72">
                {/* <Bar
                    data={processedData}
                    options={options}
                /> */}
                <Line data={chartData} options={options} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Current Month Occupied</p>
                    <p className="text-xl font-bold text-green-700">
                        {currentMonthData?.occupied || 0}
                        <span className="ml-2 text-sm font-medium text-gray-500">
                            ({occupiedPercent}%)
                        </span>
                    </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Current Month Vacant</p>
                    <p className="text-xl font-bold text-red-500">
                        {currentMonthData.vacant || 0}
                        <span className="ml-2 text-sm font-medium text-gray-500">
                            ({vacantPercent}%)
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
};

const useFinancialMonthData = (graphData = []) => {
    const sortedGraphData = useMemo(() => {
        return [...graphData].sort((a, b) => {
            if (a.year !== b.year) {
                return a.year - b.year;
            }
            const aMonth = Number(a.month);
            const bMonth = Number(b.month);
            const aAdjusted = aMonth >= 4 ? aMonth : aMonth + 12;
            const bAdjusted = bMonth >= 4 ? bMonth : bMonth + 12;

            return aAdjusted - bAdjusted;
        });
    }, [graphData]);

    const now = new Date();
    const currentMonthNumber = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const currentMonthData = useMemo(() => {
        return sortedGraphData.find(item =>
            Number(item.month) === currentMonthNumber &&
            Number(item.year) === currentYear
        ) || {
            occupied: 0,
            vacant: 0,
        };
    }, [sortedGraphData, currentMonthNumber, currentYear]);

    const totalBeds =
        currentMonthData.occupied + currentMonthData.vacant;

    const occupiedPercent = totalBeds
        ? ((currentMonthData.occupied / totalBeds) * 100).toFixed(2)
        : 0;

    const vacantPercent = totalBeds
        ? ((currentMonthData.vacant / totalBeds) * 100).toFixed(2)
        : 0;

    const getMonthYearLabel = (item) => {
        const month = Number(item.month);
        const year = Number(item.year);

        if (!month || month < 1 || month > 12 || !year) return "";
        return new Date(year, month - 1, 1).toLocaleString("en-IN", {
            month: "short",
            year: "2-digit",
        });
    };

    return {
        sortedGraphData,
        currentMonthData,
        occupiedPercent,
        vacantPercent,
        getMonthYearLabel,
    };
};

export default OccupancyChart;